import { Field, Input, Text } from "components/ui";
import { CustomerCompanyNotificationChannel } from "services/customerCompanyAdminService";
import { FieldsProps } from "./IntegrationChannelForms";

/**
 * The Microsoft Teams credentials of a notification channel: the Azure Bot's
 * App ID, a client secret of its app registration, and the Microsoft Entra
 * tenant it lives in. Kept apart from IntegrationChannelForms, which owns the
 * shared form state, validation and request body for every provider.
 */

type Translate = FieldsProps["t"];

const I18N = "app.customerCompanies.integrations.teams";

/** A Teams channel's one-line state in the channel list. */
export const teamsChannelStatus = (
  channel: Pick<CustomerCompanyNotificationChannel, "teamsSettings">,
  t: Translate,
): string =>
  channel.teamsSettings?.appId
    ? t(`${I18N}.status`, {
        defaultValue: "App ID {{appId}}",
        appId: channel.teamsSettings.appId,
      })
    : t(`${I18N}.noAppYet`, { defaultValue: "No App ID yet" });

export const TeamsFields = ({ form, onChange, existing, t }: FieldsProps) => (
  <>
    <Text fontSize="xs" color="var(--text-secondary-color)">
      {t(`${I18N}.nameHint`, {
        defaultValue:
          "The channel name is also the app's name in Teams (up to 30 characters show).",
      })}
    </Text>

    <Field
      label={t(`${I18N}.appId`, { defaultValue: "Microsoft App ID" })}
      required
      helperText={t(`${I18N}.appIdHelp`, {
        defaultValue: "On the Azure Bot's Configuration page.",
      })}
    >
      <Input
        value={form.appId}
        onChange={(e) => onChange({ appId: e.target.value })}
        placeholder="3fa85f64-5717-4562-b3fc-2c963f66afa6"
        autoComplete="off"
      />
    </Field>

    <Field
      label={t(`${I18N}.clientSecret`, { defaultValue: "Client secret" })}
      required={!existing}
      helperText={
        existing
          ? t(`${I18N}.clientSecretKeep`, {
              defaultValue:
                "Leave blank to keep the stored secret. Paste a new one before the old one expires.",
            })
          : t(`${I18N}.clientSecretHelp`, {
              defaultValue:
                "App registration → Certificates & secrets → New client secret. Copy its Value, not the Secret ID.",
            })
      }
    >
      <Input
        type="password"
        value={form.clientSecret}
        onChange={(e) => onChange({ clientSecret: e.target.value })}
        autoComplete="new-password"
      />
    </Field>

    <Field
      label={t(`${I18N}.tenantId`, { defaultValue: "Directory (tenant) ID" })}
      required
      helperText={t(`${I18N}.tenantIdHelp`, {
        defaultValue:
          "Your organisation's Microsoft Entra tenant. Only people in it can use the bot.",
      })}
    >
      <Input
        value={form.tenantId}
        onChange={(e) => onChange({ tenantId: e.target.value })}
        placeholder="a1b2c3d4-e5f6-4a5b-8c9d-0e1f2a3b4c5d"
        autoComplete="off"
      />
    </Field>
  </>
);
